import React, {useEffect, useState} from "react";
import {useConfirmDialog} from "../ConfirmDialogProvider";

/**
 * QuizQuestionEditor — question list + editor for a QUIZ lesson.
 *
 * Contract (Postman):
 *   POST  …/lessons/:lessonId/assessments/:assessmentId/questions
 *   { prompt, type: MULTIPLE_CHOICE|TRUE_FALSE|SHORT_ANSWER, position?,
 *     acceptedAnswers?: [], options?: [{ text, isCorrect }] }
 *   PATCH …/lessons/:lessonId/assessments/:assessmentId/questions/:questionId
 *   DELETE …/lessons/:lessonId/assessments/:assessmentId/questions/:questionId
 *
 * Props: { questions, loading, disabled,
 *          onCreate(payload), onUpdate(questionId, payload), onDelete(questionId) }
 */
const QUESTION_TYPES = ["MULTIPLE_CHOICE", "TRUE_FALSE", "SHORT_ANSWER"];

const EMPTY_QUESTION = {
  prompt: "",
  type: "MULTIPLE_CHOICE",
  position: "",
  acceptedAnswers: "",
  options: [
    {text: "", isCorrect: false},
    {text: "", isCorrect: false},
  ],
};

const inputClass =
  "w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-300 disabled:bg-slate-100";
const ghostBtn =
  "inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-60";
const primaryBtn =
  "inline-flex items-center gap-2 rounded-md bg-blue-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-800 disabled:opacity-60";

const toPayload = (form) => ({
  prompt: form.prompt.trim(),
  type: form.type,
  ...(form.position !== "" ? {position: Number(form.position)} : {}),
  ...(form.type === "SHORT_ANSWER"
    ? {
        acceptedAnswers: String(form.acceptedAnswers)
          .split(/[,;\n]/)
          .map((answer) => answer.trim())
          .filter(Boolean),
      }
    : {
        options: form.options
          .map((option) => ({text: String(option.text).trim(), isCorrect: Boolean(option.isCorrect)}))
          .filter((option) => option.text),
      }),
});

const validate = (form) => {
  if (!form.prompt.trim()) return "Question prompt is required.";
  const payload = toPayload(form);
  if (form.type === "SHORT_ANSWER") {
    if (!payload.acceptedAnswers.length) return "Add at least one accepted answer.";
    return null;
  }
  if (payload.options.length < 2) return "Add at least two options.";
  if (!payload.options.some((option) => option.isCorrect)) return "Mark at least one correct option.";
  return null;
};

const QuizQuestionEditor = ({questions = [], loading, disabled, onCreate, onUpdate, onDelete}) => {
  const confirm = useConfirmDialog();
  const [draft, setDraft] = useState(EMPTY_QUESTION);
  const [editingId, setEditingId] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    const message = validate(draft);
    if (message) {
      setError(message);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const result = editingId
        ? await onUpdate?.(editingId, toPayload(draft))
        : await onCreate?.(toPayload(draft));
      if (result?.success === false) {
        setError(result.error || "Could not save the question.");
        return;
      }
      if (!editingId) {
        setDraft(EMPTY_QUESTION);
        setEditorOpen(false);
      }
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (question) => {
    setEditingId(question.id);
    setEditorOpen(true);
    setError(null);
    setDraft({
      prompt: question.prompt || "",
      type: question.type || "MULTIPLE_CHOICE",
      position: question.position ?? "",
      acceptedAnswers: Array.isArray(question.acceptedAnswers)
        ? question.acceptedAnswers.join(", ")
        : question.acceptedAnswers || "",
      options:
        Array.isArray(question.options) && question.options.length
          ? question.options.map((option) => ({text: option.text || "", isCorrect: Boolean(option.isCorrect)}))
          : [...EMPTY_QUESTION.options],
    });
  };

  useEffect(() => {
    if (loading || initialized) return;
    const firstQuestion = questions[0];
    if (firstQuestion) {
      setEditingId(firstQuestion.id);
      setEditorOpen(true);
      setDraft({
        prompt: firstQuestion.prompt || "",
        type: firstQuestion.type || "MULTIPLE_CHOICE",
        position: firstQuestion.position ?? "",
        acceptedAnswers: Array.isArray(firstQuestion.acceptedAnswers)
          ? firstQuestion.acceptedAnswers.join(", ")
          : firstQuestion.acceptedAnswers || "",
        options:
          Array.isArray(firstQuestion.options) && firstQuestion.options.length
            ? firstQuestion.options.map((option) => ({
                text: option.text || "",
                isCorrect: Boolean(option.isCorrect),
              }))
            : [...EMPTY_QUESTION.options],
      });
    }
    setInitialized(true);
  }, [initialized, loading, questions]);

  const startNewQuestion = () => {
    setEditingId(null);
    setDraft({...EMPTY_QUESTION, position: String((questions || []).length + 1)});
    setError(null);
    setEditorOpen(true);
  };

  const setOption = (index, patch) =>
    setDraft((prev) => ({
      ...prev,
      options: prev.options.map((option, i) => (i === index ? {...option, ...patch} : option)),
    }));

  return (
    <div>
      <h4 className="m-0 text-xs font-bold text-slate-900">Questions ({(questions || []).length})</h4>
      {loading && (questions || []).length === 0 ? (
        <p className="mt-2 text-xs text-slate-500">Loading questions…</p>
      ) : (questions || []).length === 0 ? (
        <p className="mt-2 text-xs text-slate-500">No questions yet — add the first one below.</p>
      ) : (
        <ul className="m-0 mt-2 grid list-none gap-2 p-0">
          {(questions || []).map((question, index) => (
            <li
              key={question.id}
              className={`rounded-lg p-2.5 ${
                String(editingId) === String(question.id) ? "bg-blue-50 ring-1 ring-blue-200" : "bg-slate-50"
              }`}
            >
              <p className="m-0 text-xs font-semibold text-slate-800">
                {question.position ?? index + 1}. {question.prompt}
              </p>
              <p className="m-0 mt-1 text-[11px] text-slate-500">
                {question.type}
                {Array.isArray(question.options)
                  ? ` · ${question.options.filter((o) => o.isCorrect).length}/${question.options.length} correct`
                  : ""}
              </p>
              {!disabled ? (
                <div className="mt-2 flex gap-1.5">
                  <button type="button" className={ghostBtn} onClick={() => startEdit(question)}>Edit</button>
                  <button
                    type="button"
                    className={ghostBtn}
                    onClick={() => confirm({
                      title: "Delete this question?",
                      description: "This action cannot be undone.",
                      confirmLabel: "Delete question",
                      onConfirm: async () => {
                        const result = await onDelete?.(question.id);
                        if (result?.success && String(editingId) === String(question.id)) {
                          setEditingId(null);
                          setEditorOpen(false);
                          setDraft(EMPTY_QUESTION);
                        }
                      },
                    })}
                  >
                    Delete
                  </button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {!disabled ? (
        <button type="button" className={`${primaryBtn} mt-3`} onClick={startNewQuestion}>
          + Add new question
        </button>
      ) : null}

      {!disabled && editorOpen ? (
        <div
          className="mt-3 grid gap-2 border-t border-slate-100 pt-3"
          role="group"
          aria-label="Quiz question editor"
          onKeyDown={(event) => {
            if (event.key === "Enter" && ["INPUT", "SELECT"].includes(event.target.tagName)) submit(event);
          }}
        >
          <div className="grid gap-2 sm:grid-cols-[1fr_170px_90px]">
            <input className={inputClass} value={draft.prompt} disabled={busy}
              onChange={(e) => setDraft({...draft, prompt: e.target.value})}
              placeholder="Prompt — e.g. What is 2 + 2?" />
            <select className={inputClass} value={draft.type} disabled={busy}
              onChange={(e) => setDraft({...draft, type: e.target.value})}>
              {QUESTION_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
            <input className={inputClass} type="number" min="1" value={draft.position} disabled={busy}
              onChange={(e) => setDraft({...draft, position: e.target.value})} placeholder="Order" />
          </div>
          {draft.type === "SHORT_ANSWER" ? (
            <input className={inputClass} value={draft.acceptedAnswers} disabled={busy}
              onChange={(e) => setDraft({...draft, acceptedAnswers: e.target.value})}
              placeholder="Accepted answers, comma-separated" />
          ) : (
            <div className="grid gap-1.5">
              {draft.options.map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="w-5 text-xs font-bold text-slate-500">{String.fromCharCode(65 + index)}</span>
                  <input className={inputClass} value={option.text} disabled={busy}
                    onChange={(e) => setOption(index, {text: e.target.value})}
                    placeholder={`Option ${index + 1}`} />
                  <label className="flex shrink-0 items-center gap-1 text-[11px] text-slate-600">
                    <input type="checkbox" checked={option.isCorrect} disabled={busy}
                      onChange={(e) => setOption(index, {isCorrect: e.target.checked})} />
                    Correct
                  </label>
                  <button type="button" className={ghostBtn} disabled={busy || draft.options.length <= 2}
                    onClick={() => setDraft((prev) => ({...prev, options: prev.options.filter((_, i) => i !== index)}))}>
                    ×
                  </button>
                </div>
              ))}
              <button type="button" className={ghostBtn} disabled={busy}
                onClick={() => setDraft((prev) => ({...prev, options: [...prev.options, {text: "", isCorrect: false}]}))}>
                + Add option
              </button>
              {draft.type === "TRUE_FALSE" ? (
                <p className="m-0 text-[11px] text-slate-500">Tip: use two options — “True” and “False”.</p>
              ) : null}
            </div>
          )}
          {error ? <p className="m-0 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">{error}</p> : null}
          <div className="flex gap-2">
            <button type="button" className={primaryBtn} disabled={busy} onClick={submit}>
              {busy ? "Saving…" : editingId ? "Save question" : "Add question"}
            </button>
            <button
              type="button"
              className={ghostBtn}
              disabled={busy}
              onClick={() => {
                setEditorOpen(false);
                setEditingId(null);
                setDraft(EMPTY_QUESTION);
                setError(null);
              }}
            >
              Close editor
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default QuizQuestionEditor;
